/**
 * tourHelpers.js
 * Tiện ích dùng chung cho Sản phẩm / Tour
 */

/**
 * Kiểm tra xem một tour có phải là Tour Private (Đoàn riêng / May đo / Charter) hay không.
 * Một tour được xem là Private nếu:
 * 1. tour_type có chứa 'private' (vd: 'Private Tour', 'Private')
 * 2. code có chứa 'private' (vd: 'GIANG NAM PRIVATE')
 * 3. name có chứa từ 'private' (vd: 'Tour Thượng Hải - Hàng Châu (Private)')
 */
export const isPrivateTour = (tour) => {
  if (!tour) return false;
  const type = String(tour.tour_type || '').toLowerCase();
  if (type.includes('private')) return true;
  const code = String(tour.code || '').toLowerCase();
  if (code.includes('private')) return true;
  const name = String(tour.name || '').toLowerCase();
  if (/\bprivate\b/i.test(name)) return true;
  return false;
};

/**
 * Chuẩn hoá giới tính từ mọi nguồn:
 * - 'female', 'f', 'nữ', 'nu', 'w' -> 'Nữ'
 * - 'male', 'm', 'nam' -> 'Nam'
 * - 'chọn', '', null, undefined -> ''
 */
export const normalizeGender = (g) => {
  if (!g) return '';
  const str = String(g).trim().toLowerCase();
  if (str === 'chọn' || str === '---' || str === 'none' || str === 'chua_ro' || str === 'null' || str === 'undefined') return '';
  if (str === 'female' || str === 'f' || str === 'nữ' || str === 'nu' || str === 'w' || str.includes('nữ') || str.includes('female')) return 'Nữ';
  if (str === 'male' || str === 'm' || str === 'nam' || str.includes('nam') || str.includes('male')) return 'Nam';
  return g;
};

/**
 * Hiển thị tiếng Việt: 'Nam' | 'Nữ' | placeholder (mặc định '')
 */
export const formatGenderVN = (g, placeholder = '') => {
  const norm = normalizeGender(g);
  if (!norm) return placeholder;
  return norm;
};

/**
 * Hiển thị tiếng Anh (Namelist quốc tế): 'Male' | 'Female' | placeholder
 */
export const formatGenderEN = (g, placeholder = '') => {
  const norm = normalizeGender(g);
  if (norm === 'Nam') return 'Male';
  if (norm === 'Nữ') return 'Female';
  return placeholder;
};

/**
 * Mã 1 ký tự xuất DS Tour TQ / Visa: 'M' | 'F' | ''
 */
export const formatGenderCode = (g) => {
  const norm = normalizeGender(g);
  if (norm === 'Nam') return 'M';
  if (norm === 'Nữ') return 'F';
  return '';
};

/**
 * Trích xuất timestamp an toàn từ start_date của tour.
 * Trả về number (ms) hoặc null nếu không có ngày hoặc chuỗi ngày không hợp lệ.
 * Triệt tiêu hoàn toàn NaN trong các phép tính/so sánh.
 */
export const getTourTimestamp = (tour) => {
  if (!tour || !tour.start_date) return null;
  const ms = new Date(tour.start_date).getTime();
  return Number.isFinite(ms) ? ms : null;
};

/**
 * Kiểm tra xem tour là Quá khứ / Đã hoàn thành / Đã hủy hay chưa.
 * Phù hợp với chuẩn lọc nghiệp vụ của Điều hành Tour.
 */
export const isPastOrCompletedTour = (tour, today = new Date()) => {
  if (!tour) return false;
  if (tour.status === 'Hoàn thành' || tour.status === 'Hủy' || tour.status === 'Huỷ') return true;

  const compareDate = new Date(today);
  compareDate.setHours(0, 0, 0, 0);

  const dateStr = tour.end_date || tour.start_date;
  if (!dateStr) return false;

  const d = new Date(dateStr);
  if (!Number.isFinite(d.getTime())) return false;
  d.setHours(0, 0, 0, 0);

  return d < compareDate;
};

/**
 * Hàm so sánh Tất định (Deterministic Comparator) cho danh sách Điều hành Tour.
 * 1. Phân tầng trạng thái: Sắp tới / Mở bán lên trên, Quá khứ / Hoàn thành / Hủy xuống dưới.
 * 2. Phân tầng ngày: Tour có ngày hợp lệ lên trên, Tour thiếu ngày / ngày lỗi xuống cuối.
 * 3. So sánh thời gian:
 *    - Nhóm Sắp tới: Tăng dần theo start_date (ngày bay gần nhất lên đầu).
 *    - Nhóm Quá khứ: Giảm dần theo start_date (vừa hoàn thành gần nhất lên đầu).
 * 4. Tiêu chí phụ (Tie-breaker):
 *    - Cùng ngày HOẶC cùng thiếu ngày: Sắp xếp theo ID giảm dần (tour mới tạo lên trước).
 * Triệt tiêu 100% khả năng sinh NaN hoặc phá vỡ thuật toán TimSort.
 */
export const compareOpTours = (a, b, today = new Date()) => {
  const aPast = isPastOrCompletedTour(a, today);
  const bPast = isPastOrCompletedTour(b, today);

  // 1. Phân tầng trạng thái
  if (aPast && !bPast) return 1;
  if (!aPast && bPast) return -1;

  const timeA = getTourTimestamp(a);
  const timeB = getTourTimestamp(b);

  // 2. Phân tầng ngày hợp lệ
  if (timeA !== null && timeB === null) return -1;
  if (timeA === null && timeB !== null) return 1;

  // 3. So sánh thời gian nếu cả hai đều có ngày hợp lệ và khác ngày
  if (timeA !== null && timeB !== null && timeA !== timeB) {
    return (aPast && bPast) ? (timeB - timeA) : (timeA - timeB);
  }

  // 4. Tie-breaker theo ID giảm dần (tính tất định 100%)
  const idA = Number(a?.id) || 0;
  const idB = Number(b?.id) || 0;
  return idB - idA;
};
